// Shared helpers for the §6.2–§6.5 workflow forms.

import { dubaiDate } from './format';

const ENTITY = 'dubaiotc' as const;

/**
 * Build a Formance treasury account path matching the BFF's lib/assets.ts.
 * The `slug` may be a single segment ('office') or a dot-separated hierarchy
 * ('exchange.binance.dxb' → expanded to colons in the Formance path).
 */
export function treasuryPath(slug: string, asset: string, entity: string = ENTITY): string {
  // USD and EUR are FIAT. This tested only AED and INR, so every other fiat fell
  // through to 'crypto' and built a path the BFF never uses: the office's USD
  // resolved to …:cash:office:crypto:usd while the real account is
  // …:cash:office:fiat:usd. Effect: Move money could not shift the office's
  // 14,700 USD at all (400 new_rail_asset naming an account nobody has seen),
  // and a USD receipt would have opened the phantom twin — permanently, because
  // Formance is append-only. isFiat() below is the same set the BFF's
  // ASSET_CATEGORY uses; assetCategory() already wraps it. Use it.
  const category = assetCategory(asset);
  const ASSET_SLUG: Record<string, string> = {
    AED:     'aed',
    INR:     'inr',
    USDTTRC: 'usdt-trc',
    USDTERC: 'usdt-erc',
    USDTBEP: 'usdt-bep',
    // These four were selectable in ASSETS below but had no slug, so the
    // `?? asset.toLowerCase()` fallback built a DIFFERENT account path than the
    // BFF: USDTSOL became `usdtsol` here and `usdt-sol` there — two accounts for
    // one balance, each internally consistent. This map must mirror
    // bff/src/lib/assets.ts ASSET_SLUG exactly, because both sides mint paths.
    USDTSOL: 'usdt-sol',
    USDCTRC: 'usdc-trc',
    USDCERC: 'usdc-erc',
    USDCBEP: 'usdc-bep',
    // USDCBASE was missing here on 19 Sept 2026 and the fallback below built
    // `usdcbase` while the Worker spells it `usdc-base`: the first receipt on
    // Hot wallet · Base was refused as "no desk account called Hot-base ·
    // USDCBASE". scripts/check-asset-scales.mjs now fails the build when this
    // copy and the Worker's table disagree, and the Worker reads the bare
    // spelling too — but the table is still meant to be identical.
    USDCBASE: 'usdc-base',
    USD:     'usd',
    EUR:     'eur',
    BTC:     'btc',
    ETH:     'eth',
    SOL:     'sol',
    USDT:    'usdt',
    USDC:    'usdc',
    XLM:     'xlm',
    BNB:     'bnb',
    TRX:     'trx',
    GRAM:    'gram',
    APT:     'apt',
  };
  const assetSlug = ASSET_SLUG[asset] ?? asset.toLowerCase();
  const segs = slug.split('.');
  // books-v2 crypto wallets are `treasury:{ent}:crypto:{wallet}:{asset}` — the
  // KIND segment already IS the category, so appending it again mints a phantom
  // twin. This mirrors bff/src/lib/assets.ts::treasuryAccount, which has carried
  // the guard since 2026-08-12; this copy did not, so the two sides built
  // DIFFERENT paths for the same rail. On 2026-08-22 that put 29,577 USDT on
  // `treasury:dubaiotc:crypto:hot:crypto:usdt-trc` instead of `…:crypto:hot:…`.
  const skipCategory = segs[0] === category;
  return ['treasury', entity, ...segs, ...(skipCategory ? [] : [category]), assetSlug].join(':');
}

export function customerPath(slug: string, entity: string = ENTITY): string {
  return `customer:${entity}:${slug}`;
}

export function counterpartyPath(slug: string, entity: string = ENTITY): string {
  return `counterparty:${entity}:${slug}`;
}

/**
 * The party's REAL account, chosen by its type.
 *
 * A merchant desk (HK, JP Office, Loki OTC, Thanos, Deepak Patel, Billa OTC)
 * lives under `counterparty:`; everyone else under `customer:`. Posting to the
 * wrong twin does not fail — it silently opens a SECOND account for the same
 * party, so their position is split in half and every per-party view is wrong.
 *
 * Found live 2026-08-22: a 70,000 USDC receipt from Loki OTC landed on
 * customer:dubaiotc:loki-otc while Loki's real position (including -19,520
 * USDT-ERC) sat on counterparty:dubaiotc:loki-otc. All six merchants had been
 * split the same way, because the caller hard-coded customerPath().
 */
export function partyPath(
  slug: string,
  partyType: string | undefined,
  entity: string = ENTITY,
): string {
  return partyType === 'counterparty' ? counterpartyPath(slug, entity) : customerPath(slug, entity);
}

/** A manager's cheque id, minted ONCE per form fill so a retry after a lost
 *  response lands on the same `${deal_id}:book` reference instead of booking a
 *  second deposit under a new deal (3 Sept audit). */
export function newChequeId(): string {
  return `MC-${dubaiDate()}-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;
}

/** Common date-stamped reference IDs for receipts/payments/transfers/adjustments.
 *  `RCPT-2026-09-03-K7Q2X` — prefix, Dubai day, random tail. `at` is the
 *  instant the day is read from; absent = now. 5 Sept 2026: a back-dated
 *  movement (Loki's 3 Sept money) carries the day it moved, so its reference
 *  reads like that day's other entries and not like the day it was typed. */
export function makeReference(prefix: string, at?: string | number | Date): string {
  // An EMPTY `at` means now, the same as an absent one (review, 5 Sept 2026):
  // a screen passing its date field straight through handed '' here, and
  // dubaiDate('') is '' (Date.parse('') is NaN), so the reference came out
  // `RCPT--XXXXX` — a stamp with no day in it.
  const day = at ? dubaiDate(at) : dubaiDate();
  return `${prefix}-${day}-${Math.random().toString(36).slice(2, 7).toUpperCase()}`;
}

// Mirrors admin.asset_registry. ETH added 2026-08-20 for the customer ETH -> AED
// flow: it was registered in the BFF and the database but absent here, so it
// could not be selected anywhere in the console. USDCBASE the same way on
// 19 Sept 2026: the slug map above knew it, this list did not, and no picker
// (Book trade, Sub-broker trade, Record money, Move, Convert…) could offer
// USDC·BASE while the 4,62,842 USDC Loki sent sat on Hot wallet · Base.
export const ASSETS = ['AED', 'INR', 'USD', 'EUR', 'USDTTRC', 'USDTERC', 'USDCERC', 'USDTBEP', 'USDCBEP', 'USDCBASE', 'USDTSOL', 'BTC', 'ETH', 'SOL'] as const;
export type AssetCode = typeof ASSETS[number];

const FIAT_ASSETS = new Set(['AED', 'INR', 'USD', 'EUR']);
export function isFiat(asset: string): boolean { return FIAT_ASSETS.has(asset); }

/** THE CURRENCY A DEAL'S SPREAD IS IN — its margin book's, never the coin it took (Mechanisms via the Command Centre, 1 Nov 2026:
 *  a conversion's AED margin read "… USDT"). The route's own `spread_asset` when it sends one; else the Worker's rule
 *  (bff/src/routes/trades.ts, bookQuote): a coin against money is margined in the money side (the inventory book's quote
 *  currency); a coin for a coin (a venue conversion) in AED (lib/inventory.ts CARRY_QUOTE); money for money has no book, so
 *  the taken side, as before. */
export function spreadAsset(d: { asset_take: string; asset_give: string; spread_asset?: string | null }): string {
  if (d.spread_asset) return d.spread_asset;
  const take = isFiat(d.asset_take), give = isFiat(d.asset_give);
  if (take !== give) return take ? d.asset_take : d.asset_give;
  return take ? d.asset_take : 'AED';
}

/**
 * Operator's 95%-case rails. Used to pre-fill form pickers so the operator
 * lands on the right rail without having to search every time.
 * Edit these here whenever the primary hot wallet rotates.
 */
export const DEFAULT_RAILS = {
  cash:   { slug: 'cash.office', asset: 'AED' as const     },  // office cash (books-v2)
  hotTrc: { slug: 'crypto.hot-trc', asset: 'USDTTRC' as const }, // hot wallet, TRC20 (renamed 2026-08-22, one rail per chain)
};
export function assetCategory(asset: string): 'fiat' | 'crypto' {
  return isFiat(asset) ? 'fiat' : 'crypto';
}

// ─────────────────────────────────────────────────────────────────────────────
// Currency-bucket rules (Layer A — physics).
// Single source of truth for which rail kinds can hold which asset categories.
// MUST stay in sync with bff/src/lib/buckets.ts RAIL_BUCKETS.
// ─────────────────────────────────────────────────────────────────────────────
export const RAIL_BUCKETS: Record<string, Array<'fiat' | 'crypto'>> = {
  // `crypto` is the kind every real desk wallet uses (crypto.hot,
  // crypto.lokis-ledger). It was MISSING here, so compatibleKinds() returned a
  // list without it and the pickers filtered out the very wallets a crypto
  // asset needs — while leaving legacy/other rails in. Reported 2026-08-22
  // ("why am I seeing all the treasury parties and not the relevant ones").
  crypto:   ['crypto'],
  exchange: ['crypto'],
  hot:      ['crypto'],
  cold:     ['crypto'],
  cash:     ['fiat'],
  bank:     ['fiat'],
  angadiya: ['fiat'],
  legacy:   ['fiat', 'crypto'],   // frozen, read-only-ish
  other:    ['fiat', 'crypto'],
};

/** All rail kinds compatible with this asset (drives Layer-A picker filters). */
export function compatibleKinds(asset: string): string[] {
  const cat = assetCategory(asset);
  return Object.entries(RAIL_BUCKETS)
    .filter(([, cats]) => cats.includes(cat))
    .map(([kind]) => kind);
}

/** Same shape, but takes the category directly. Useful when you know the side's
 *  family before picking a specific asset (e.g. customer_buy take side = fiat). */
export function kindsForCategory(cat: 'fiat' | 'crypto'): string[] {
  return Object.entries(RAIL_BUCKETS)
    .filter(([, cats]) => cats.includes(cat))
    .map(([kind]) => kind);
}

/**
 * Derive payment_method from the treasury slug's kind.
 *   bank.*      → bank-transfer
 *   cash.*      → cash
 *   exchange.*  → crypto (we sent / received via the exchange's rails)
 *   hot.* / cold.* → crypto (self-custody chain)
 *   angadiya.*  → cash    (Angadiya merchant delivers INR cash physically)
 */
export function derivePaymentMethod(slug: string, override?: 'manager-cheque'): string {
  if (override) return override;
  const kind = slug.split('.')[0] ?? '';
  switch (kind) {
    case 'bank':     return 'bank-transfer';
    case 'cash':     return 'cash';
    case 'angadiya': return 'cash';
    case 'exchange':
    case 'hot':
    case 'cold':     return 'crypto';
    default:         return 'other';
  }
}
